import React, { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { NavLink } from "react-router-dom";
import "./WhosItFor.scss";
import { accountCategoryURL, registrationFormURL } from "../../components/helpers/constant-words";
import useTheme from "../../components/useTheme/useTheme";

const WhosItFor = ({ openPopup }) => {
  const { theme } = useTheme();
  const projectOwnersTrackRef = useRef(null);
  const professionalsTrackRef = useRef(null);
  const businessTagsRow1Ref = useRef(null);
  const businessTagsRow2Ref = useRef(null);

  const projectOwnersTags = [
    "Home owners",
    "Café & restaurant owners",
    "Office & workspace owners",
    "Retail Operators",
    "Landlords",
    "Hospitality owners",
    "Education institute owners"
  ];
  const professionalsTags = [
    "Architect",
    "Interior Designer",
    "Furniture Designer",
    "Lighting Designer",
    "BIM professionals",
    "Artists",
    "Freelancers",
    "Fresh Graduates",
    "Students",
    "Interns"
  ];

  // Duplicate tags for seamless loop
  const projectOwnersAllTags = [...projectOwnersTags, ...projectOwnersTags];
  const professionalsAllTags = [...professionalsTags, ...professionalsTags];

  // Animation effect for Project Owners tags
  useEffect(() => {
    const track = projectOwnersTrackRef.current;
    if (!track) return;

    let offset = 0;
    const speed = 0.3;
    let frameId;

    const animate = () => {
      offset -= speed;
      const width = track.scrollWidth / 2;

      if (Math.abs(offset) >= width) {
        offset = 0;
      }

      track.style.transform = `translateX(${offset}px)`;
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, []);

  // Animation effect for Professionals tags
  useEffect(() => {
    const track = professionalsTrackRef.current;
    if (!track) return;

    let offset = 0;
    const speed = 0.3;
    let frameId;

    const animate = () => {
      offset -= speed;
      const width = track.scrollWidth / 2;

      if (Math.abs(offset) >= width) {
        offset = 0;
      }

      track.style.transform = `translateX(${offset}px)`;
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, []);

  // Business tags split into two rows
  const businessTagsRow1 = [
    "Architecture Firms",
    "Interior Design Firms",
    "HVAC Consultants",
    "Execution",
    "Turnkey Contracting",
    "Furniture Brand"
  ];
  const businessTagsRow2 = [
    "Showrooms",
    "Distributors",
    "Flooring Solutions",
    "Marketing Agency",
    "PR Agency",
    "Photographers"
  ];

  // Duplicate for seamless loop
  const businessTagsRow1All = [...businessTagsRow1, ...businessTagsRow1];
  const businessTagsRow2All = [...businessTagsRow2, ...businessTagsRow2];

  // Animation effect for Business Tags Row 1 (scroll left)
  useEffect(() => {
    const track = businessTagsRow1Ref.current;
    if (!track) return;

    let offset = 0;
    const speed = 0.3;
    let frameId;

    const animate = () => {
      offset -= speed;
      const width = track.scrollWidth / 2;

      if (Math.abs(offset) >= width) {
        offset = 0;
      }

      track.style.transform = `translateX(${offset}px)`;
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, []);

  // Animation effect for Business Tags Row 2 (scroll right)
  useEffect(() => {
    const track = businessTagsRow2Ref.current;
    if (!track) return;

    let offset = 0;
    const speed = 0.3;
    let frameId;
    const width = track.scrollWidth / 2;
    offset = -width; // Start from the end

    const animate = () => {
      offset += speed;

      if (offset >= 0) {
        offset = -width;
      }

      track.style.transform = `translateX(${offset}px)`;
      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, []);

  return (
    <>
      <section className="whos-it-for-section">
        <div className="whos-it-for-container">
          {/* Header */}
          <div className="header-section">
            <h2 className="header-title">Who is it for?</h2>
            <p
              className={`header-description  ${
                theme === "dark" ? "dark_mode_text_muted--theme-styles" : ""
              }`}
            >
              Archinza is a{" "}
              <span className="orange-text">discovery platform</span> for everyone
              in the AECD ecosystem from{" "}
              <span className="semibold-text">
                professionals and businesses to students and design-curious users.
              </span>
            </p>
          </div>

          {/* Two Column Layout */}
          <div className="two-column-grid">
            {/* Project Owners Card */}
            <div
              className="card"
              style={{
                backgroundColor: theme === "dark" ? "#ffffff1a" : "",
              }}
            >
              {/* Category Header */}
              <h3 className="category-header">Individuals &amp; Project Owners</h3>

              {/* Image Container */}
              <div className="image-container">
                <img
                  src="/images/ProjectOwners.png"
                  alt="Project owners discovering professionals and products"
                  className="card-image"
                />
              </div>

              {/* Content */}
              <div className="content-section">
                {/* Tags */}
                <div className="tags-container">
                  <div className="scrolling-tags-wrapper">
                    <div className="scrolling-tags-track" ref={projectOwnersTrackRef}>
                      {projectOwnersAllTags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="tag"
                          style={{
                            backgroundColor: theme === "dark" ? "#ffffff1a" : "",
                            color: theme === "dark" ? "#ffffffcc" : "",
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <h4 className="content-title">Building or renovating a space?</h4>

                <p
                  className={`content-description  ${
                    theme === "dark" ? "dark_mode_text_muted--theme-styles" : ""
                  }`}
                >
                  Discover the right professionals, products, and answers for your project needs - all in one place.
                </p>

                {/* Buttons */}
                <div className="buttons-container">
                  {/* <Link to={accountCategoryURL}> */}
                  <div className="button-wrapper">
                    <Link to={registrationFormURL}>
                      <button className="primary-button outlined">
                        Get Started
                        <ArrowRight className="arrow-icon" />
                      </button>
                    </Link>
                    <span className="button-subtext">Free for a limited time only</span>
                  </div>
                  <NavLink to="/personal" className="text-link">
                    Know More
                    <ArrowRight className="arrow-icon" />
                  </NavLink>
                </div>
              </div>
            </div>

            {/* Professionals & Students Card */}
            <div
              className="card"
              style={{
                backgroundColor: theme === "dark" ? "#ffffff1a" : "",
              }}
            >
              {/* Category Header */}
              <h3 className="category-header">Professionals &amp; Students</h3>

              {/* Image Container */}
              <div className="image-container">
                <img
                  src="/images/Professionals.png"
                  alt="Professionals and students exploring opportunities"
                  className="card-image"
                />
              </div>

              {/* Content */}
              <div className="content-section">
                {/* Tags */}
                <div className="tags-container">
                  <div className="scrolling-tags-wrapper">
                    <div className="scrolling-tags-track" ref={professionalsTrackRef}>
                      {professionalsAllTags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="tag"
                          style={{
                            backgroundColor: theme === "dark" ? "#ffffff1a" : "",
                            color: theme === "dark" ? "#ffffffcc" : "",
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <h4 className="content-title">Working or studying in AECD Industry?</h4>

                <p
                  className={`content-description  ${
                    theme === "dark" ? "dark_mode_text_muted--theme-styles" : ""
                  }`}
                >
                  Discover products, explore businesses that are hiring, get answers to technical questions, and find courses to upskill.
                </p>

                {/* Buttons */}
                <div className="buttons-container">
                  {/* <Link to={accountCategoryURL}> */}
                  <div className="button-wrapper">
                    <Link to={registrationFormURL}>
                      <button className="primary-button outlined">
                        Get Started
                        <ArrowRight className="arrow-icon" />
                      </button>
                    </Link>
                    <span className="button-subtext">Free for a limited time only</span>
                  </div>
                  <NavLink to="/personal" className="text-link">
                    Know More
                    <ArrowRight className="arrow-icon" />
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Section */}
      <div className="business-section-wrapper">
        <section className="business-section">
          <div className="business-container">
            <h2 className="business-title">
              <span style={{ whiteSpace: "nowrap" }}>Are you a business,</span><br />
              <span style={{ whiteSpace: "nowrap" }}>design firm or brand in the</span><br />
              <span style={{ whiteSpace: "nowrap" }}>Design &amp; Build Industry?</span>
            </h2>

            <div className="business-image-container">
              <img
                src="/images/Business_section.png"
                alt="Business platform showcasing offerings"
                className="business-image"
              />
            </div>

            <div className="business-content">
              <p className="business-subtitle">
                Register as an Early Adopter on Archinza, a platform built exclusively for the Design & Build industry. Get limited early access with premium features at an introductory price, before the full product launch.
              </p>

              {/* Tags - Two Row Scrolling */}
              <div className="business-tags-scrolling">
                {/* Row 1 - scrolls left */}
                <div className="business-scrolling-wrapper">
                  <div className="business-scrolling-track" ref={businessTagsRow1Ref}>
                    {businessTagsRow1All.map((tag, idx) => (
                      <span key={idx} className="business-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                {/* Row 2 - scrolls right */}
                <div className="business-scrolling-wrapper">
                  <div className="business-scrolling-track" ref={businessTagsRow2Ref}>
                    {businessTagsRow2All.map((tag, idx) => (
                      <span key={idx} className="business-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="business-buttons">
                {/* <Link to={accountCategoryURL}>
                  <button className="business-primary-button">
                    Register as a Business
                    <ArrowRight className="arrow-icon" />
                  </button>
                </Link> */}
                <div className="button-wrapper">
                  <a href="https://business.archinza.com" target="_blank" rel="noreferrer">
                    <button className="business-primary-button">
                      Register here
                      <ArrowRight className="arrow-icon" />
                    </button>
                  </a>
                  <span className="button-subtext">Free for a limited time only</span>
                </div>
{/* <NavLink to="/business" className="business-text-link">
                  Know More
                  <ArrowRight className="arrow-icon" />
                </NavLink> */}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default WhosItFor;
